import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRestaurantContext } from '@/context/RestaurantContext';
import { useAuth } from '@/context/auth';
import { DateRange } from 'react-day-picker';

interface AnalyticsData {
  totalOrders: number;
  totalRevenue: number;
  deliveryOrders: number;
  pickupOrders: number;
  avgOrderValue: number;
  newCustomers: number;
  rawData: any[];
  deliveryRevenue: number;
  pickupRevenue: number;
  deliveryAvgOrderValue: number;
  pickupAvgOrderValue: number;
  deliveryNewCustomers: number;
  pickupNewCustomers: number;
}

interface ChangeMetrics {
  totalOrders: { value: string; trend: 'up' | 'down' | 'neutral' };
  totalRevenue: { value: string; trend: 'up' | 'down' | 'neutral' };
  avgOrderValue: { value: string; trend: 'up' | 'down' | 'neutral' };
  newCustomers: { value: string; trend: 'up' | 'down' | 'neutral' };
}

interface ComparisonMetrics {
  totalPumpdRevenue: number;
  totalUberEatsRevenue: number;
  totalCustomerSavings: number;
  totalRestaurantProfit: number;
  deliveryOrderCount: number;
  avgCustomerSavings: number;
  avgRestaurantProfit: number;
  revenueDifference: number;
  revenuePercentage: number;
}

const createEmptyAnalyticsData = (): AnalyticsData => ({
  totalOrders: 0,
  totalRevenue: 0,
  deliveryOrders: 0,
  pickupOrders: 0,
  avgOrderValue: 0,
  newCustomers: 0,
  rawData: [],
  deliveryRevenue: 0,
  pickupRevenue: 0,
  deliveryAvgOrderValue: 0,
  pickupAvgOrderValue: 0,
  deliveryNewCustomers: 0,
  pickupNewCustomers: 0,
});

export const useAnalyticsDashboard = (
  timeframe: string | DateRange = '30',
  orderTypeFilter: 'all' | 'delivery' | 'pickup' = 'all'
) => {
  const [currentData, setCurrentData] = useState<AnalyticsData | null>(null);
  const [previousData, setPreviousData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [restaurantMarkups, setRestaurantMarkups] = useState<Record<string, number>>({});
  const { selectedRestaurants } = useRestaurantContext();
  const { user } = useAuth();
  
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedRestaurants?.length) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Calculate date ranges for current and previous periods
        let currentEndDate = new Date();
        let currentStartDate = new Date();
        let periodDays = 30; // Default period for calculating previous period
        
        // Handle different timeframe types
        if (typeof timeframe === 'object' && timeframe.from && timeframe.to) {
          // Custom date range
          currentStartDate = new Date(timeframe.from);
          currentEndDate = new Date(timeframe.to);
          periodDays = Math.ceil((currentEndDate.getTime() - currentStartDate.getTime()) / (1000 * 60 * 60 * 24));
        } else if (typeof timeframe === 'string') {
          // Preset options
          switch (timeframe) {
            case '7':
              currentStartDate.setDate(currentEndDate.getDate() - 7);
              periodDays = 7;
              break;
            case '30':
              currentStartDate.setDate(currentEndDate.getDate() - 30);
              periodDays = 30;
              break;
            case '90':
              currentStartDate.setDate(currentEndDate.getDate() - 90);
              periodDays = 90;
              break;
            case '365':
              currentStartDate.setDate(currentEndDate.getDate() - 365);
              periodDays = 365;
              break;
            case 'ytd':
              // Year-to-date: from January 1st to today
              currentStartDate = new Date(currentEndDate.getFullYear(), 0, 1);
              periodDays = Math.ceil((currentEndDate.getTime() - currentStartDate.getTime()) / (1000 * 60 * 60 * 24));
              break;
            case 'all': {
              // All-time: we'll need to fetch the earliest order date
              const { data: earliestOrder } = await supabase
                .from('orders')
                .select('date_and_time')
                .order('date_and_time', { ascending: true })
                .limit(1);
              
              if (earliestOrder && earliestOrder.length > 0) {
                currentStartDate = new Date(earliestOrder[0].date_and_time);
                periodDays = Math.ceil((currentEndDate.getTime() - currentStartDate.getTime()) / (1000 * 60 * 60 * 24));
              } else {
                // Default to last 30 days if no orders found
                currentStartDate.setDate(currentEndDate.getDate() - 30);
                periodDays = 30;
              }
              break;
            }
            default:
              // Default to 30 days
              currentStartDate.setDate(currentEndDate.getDate() - 30);
              periodDays = 30;
          }
        }
        
        // Calculate previous period dates
        const previousEndDate = new Date(currentStartDate);
        previousEndDate.setDate(previousEndDate.getDate() - 1);
        const previousStartDate = new Date(previousEndDate);
        previousStartDate.setDate(previousStartDate.getDate() - periodDays);
        
        // Fetch restaurant markups
        const restaurantIds = selectedRestaurants.includes('all') 
          ? user?.restaurants?.map(r => r.id) || []
          : selectedRestaurants;
          
        if (restaurantIds.length > 0 && !restaurantIds.includes('all')) {
          const { data: markupData, error: markupError } = await supabase
            .from('restaurants_master')
            .select('restaurant_id, ubereats_markup')
            .in('restaurant_id', restaurantIds);
            
          if (!markupError && markupData) {
            const markups: Record<string, number> = {};
            markupData.forEach(restaurant => {
              markups[restaurant.restaurant_id] = restaurant.ubereats_markup || 0;
            });
            setRestaurantMarkups(markups);
          }
        }
        
        // Fetch data for current period
        const currentPeriodData = await fetchPeriodData(
          currentStartDate,
          currentEndDate,
          selectedRestaurants,
          orderTypeFilter
        );
        setCurrentData(currentPeriodData);
        
        // Fetch data for previous period
        const previousPeriodData = await fetchPeriodData(
          previousStartDate,
          previousEndDate,
          selectedRestaurants,
          orderTypeFilter
        );
        setPreviousData(previousPeriodData);
      } catch (err: any) {
        console.error('Error fetching analytics data:', err);
        setError('Failed to load analytics data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [selectedRestaurants, timeframe, orderTypeFilter, user]);
  
  const fetchPeriodData = async (
    startDate: Date,
    endDate: Date,
    restaurantIds: string[],
    orderType: 'all' | 'delivery' | 'pickup' = 'all'
  ): Promise<AnalyticsData> => {
    try {
      // Get user's accessible restaurant IDs from the auth context
      const userAccessibleRestaurantIds = user?.restaurants?.map(r => r.id) || [];
      
      if (userAccessibleRestaurantIds.length === 0) {
        console.warn('No accessible restaurants found for user');
        return createEmptyAnalyticsData();
      }

      // Query orders table for the selected restaurant and date range
      let query = supabase
        .from('orders')
        .select('*')
        .gte('date_and_time', startDate.toISOString())
        .lte('date_and_time', endDate.toISOString());
      
      // Apply order type filter
      if (orderType !== 'all') {
        query = query.eq('service_type', orderType);
      }
        
      // If 'all' is selected, filter by all accessible restaurants
      // Otherwise, filter by selected restaurants (ensuring they're accessible)
      if (restaurantIds.includes('all')) {
        if (userAccessibleRestaurantIds.length > 0) {
          query = query.in('pumpd_restaurant_id', userAccessibleRestaurantIds);
        }
      } else if (restaurantIds.length > 0) {
        // Filter by selected restaurants (only those the user has access to)
        const validRestaurantIds = restaurantIds.filter(id => 
          userAccessibleRestaurantIds.includes(id)
        );
        
        if (validRestaurantIds.length > 0) {
          query = query.in('pumpd_restaurant_id', validRestaurantIds);
        } else {
          // No valid restaurants selected, return empty data
          console.warn('No valid restaurants in selection');
          return createEmptyAnalyticsData();
        }
      } else {
        // No restaurants selected, return empty data
        console.warn('No restaurants selected');
        return createEmptyAnalyticsData();
      }
      
      const { data: orderData, error: orderError } = await query;
      
      if (orderError) throw orderError;
      
      // Get new customers (first order in this period)
      let newCustomers = 0;
      try {
        // Query for customers whose first order was within this period
        let customersQuery = supabase
          .from('customer_restaurant_activity')
          .select('customer_id')
          .gte('first_order_date', startDate.toISOString())
          .lte('first_order_date', endDate.toISOString());

        // Apply the same restaurant filtering logic as orders
        if (restaurantIds.includes('all')) {
          if (userAccessibleRestaurantIds.length > 0) {
            customersQuery = customersQuery.in('restaurant_id', userAccessibleRestaurantIds);
          }
        } else if (restaurantIds.length > 0) {
          const validRestaurantIds = restaurantIds.filter(id => 
            userAccessibleRestaurantIds.includes(id)
          );
          
          if (validRestaurantIds.length > 0) {
            customersQuery = customersQuery.in('restaurant_id', validRestaurantIds);
          }
        }

        const { data: newCustomersData, error: newCustomersError } = await customersQuery;

        if (newCustomersError) throw newCustomersError;
        
        // Get unique count of new customers
        newCustomers = newCustomersData ? 
          new Set(newCustomersData.map(item => item.customer_id)).size : 
          0;
      } catch (err) {
        console.error('Error fetching new customers:', err);
        // Fallback to simpler method if the advanced query fails
        if (orderData && orderData.length > 0) {
          const uniqueCustomerEmails = new Set(
            orderData
              .filter(order => order.customer_email)
              .map(order => order.customer_email.toLowerCase())
          );
          newCustomers = uniqueCustomerEmails.size;
        }
      }
      
      if (orderData && orderData.length > 0) {
        const deliveryOrdersData = orderData.filter(order => order.service_type === 'delivery');
        const pickupOrdersData = orderData.filter(order => order.service_type === 'pickup');
        
        const deliveryOrders = deliveryOrdersData.length;
        const pickupOrders = pickupOrdersData.length;
        const totalOrders = orderData.length;
        
        const totalRevenue = orderData.reduce(
          (sum, order) => sum + (parseFloat(order.cart_value) || 0),
          0
        );
        
        const deliveryRevenue = deliveryOrdersData.reduce(
          (sum, order) => sum + (parseFloat(order.cart_value) || 0),
          0
        );
        
        const pickupRevenue = pickupOrdersData.reduce(
          (sum, order) => sum + (parseFloat(order.cart_value) || 0),
          0
        );
        
        const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
        const deliveryAvgOrderValue = deliveryOrders > 0 ? deliveryRevenue / deliveryOrders : 0;
        const pickupAvgOrderValue = pickupOrders > 0 ? pickupRevenue / pickupOrders : 0;
        
        // Calculate new customers by order type
        let deliveryNewCustomers = 0;
        let pickupNewCustomers = 0;
        
        // Create a set of unique customers for each order type
        const deliveryCustomers = new Set<string>();
        const pickupCustomers = new Set<string>();
        
        orderData.forEach(order => {
          const email = order.customer_email?.toLowerCase();
          if (email) {
            if (order.service_type === 'delivery') {
              deliveryCustomers.add(email);
            } else if (order.service_type === 'pickup') {
              pickupCustomers.add(email);
            }
          }
        });
        
        // For simplicity, count unique customers by order type
        // This is an approximation since we don't have access to first order dates here
        deliveryNewCustomers = deliveryCustomers.size;
        pickupNewCustomers = pickupCustomers.size;
        
        return {
          totalOrders,
          totalRevenue,
          deliveryOrders,
          pickupOrders,
          avgOrderValue,
          newCustomers,
          rawData: orderData,
          deliveryRevenue,
          pickupRevenue,
          deliveryAvgOrderValue,
          pickupAvgOrderValue,
          deliveryNewCustomers,
          pickupNewCustomers,
        };
      } else {
        // No data found
        return createEmptyAnalyticsData();
      }
    } catch (error) {
      console.error('Unexpected error fetching analytics data:', error);
      return createEmptyAnalyticsData();
    }
  };
  
  const dailyData = useMemo(() => {
    if (!currentData?.rawData?.length) return [];
    
    const dailyMap = new Map();
    
    // Group orders by date
    currentData.rawData.forEach(order => {
      const d = new Date(order.date_and_time);
      const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const currentValue = dailyMap.get(date) || { 
        date, 
        revenue: 0, 
        orders: 0,
        customer_savings: 0,
        restaurant_profit_increase: 0,
        pickupOrders: 0,
        deliveryOrders: 0,
        pickupRevenue: 0,
        deliveryRevenue: 0
      };
      
      const isDelivery = order.service_type === 'delivery';
      const isPickup = order.service_type === 'pickup';
      const orderValue = parseFloat(order.cart_value) || 0;
      
      dailyMap.set(date, {
        date,
        revenue: currentValue.revenue + orderValue,
        orders: currentValue.orders + 1,
        customer_savings: currentValue.customer_savings + (
          isDelivery ? (parseFloat(order.difference_in_cost_to_customer) || 0) : 0
        ),
        restaurant_profit_increase: currentValue.restaurant_profit_increase + (
          isDelivery ? (parseFloat(order.difference_in_restaurant_income) || 0) : 0
        ),
        pickupOrders: currentValue.pickupOrders + (isPickup ? 1 : 0),
        deliveryOrders: currentValue.deliveryOrders + (isDelivery ? 1 : 0),
        pickupRevenue: currentValue.pickupRevenue + (isPickup ? orderValue : 0),
        deliveryRevenue: currentValue.deliveryRevenue + (isDelivery ? orderValue : 0)
      });
    });
    
    // Convert to array and sort by date
    return Array.from(dailyMap.values())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [currentData?.rawData]);
  
  const orderTypeData = useMemo(() => {
    if (!currentData || !currentData.rawData) return [];
    
    // Calculate total values and averages for each order type
    let deliveryTotal = 0;
    let pickupTotal = 0;
    let deliveryCount = 0;
    let pickupCount = 0;
    
    currentData.rawData.forEach(order => {
      const orderValue = parseFloat(order.cart_value) || 0;
      if (order.service_type === 'delivery') {
        deliveryTotal += orderValue;
        deliveryCount++;
      } else if (order.service_type === 'pickup') {
        pickupTotal += orderValue;
        pickupCount++;
      }
    });
    
    return [
      { 
        name: 'Delivery', 
        value: deliveryCount,
        totalValue: deliveryTotal,
        avgOrderValue: deliveryCount > 0 ? deliveryTotal / deliveryCount : 0
      },
      { 
        name: 'Pickup', 
        value: pickupCount,
        totalValue: pickupTotal,
        avgOrderValue: pickupCount > 0 ? pickupTotal / pickupCount : 0
      },
    ];
  }, [currentData]);
  
  const comparisonMetrics = useMemo<ComparisonMetrics>(() => {
    if (!currentData?.rawData?.length) {
      return {
        totalPumpdRevenue: 0,
        totalUberEatsRevenue: 0,
        totalCustomerSavings: 0,
        totalRestaurantProfit: 0,
        deliveryOrderCount: 0,
        avgCustomerSavings: 0,
        avgRestaurantProfit: 0,
        revenueDifference: 0,
        revenuePercentage: 0
      };
    }
    
    let totalPumpdRevenue = 0;
    let totalUberEatsRevenue = 0;
    let totalCustomerSavings = 0;
    let totalRestaurantProfit = 0;
    let deliveryOrderCount = 0;
    
    currentData.rawData.forEach(order => {
      if (order.service_type === 'delivery') {
        totalPumpdRevenue += parseFloat(order.pumpd_net_income) || 0;
        totalUberEatsRevenue += parseFloat(order.ubereats_net_income) || 0;
        totalCustomerSavings += parseFloat(order.difference_in_cost_to_customer) || 0;
        totalRestaurantProfit += parseFloat(order.difference_in_restaurant_income) || 0;
        deliveryOrderCount++;
      }
    });
    
    return {
      totalPumpdRevenue,
      totalUberEatsRevenue,
      totalCustomerSavings,
      totalRestaurantProfit,
      deliveryOrderCount,
      avgCustomerSavings: deliveryOrderCount ? totalCustomerSavings / deliveryOrderCount : 0,
      avgRestaurantProfit: deliveryOrderCount ? totalRestaurantProfit / deliveryOrderCount : 0,
      revenueDifference: totalPumpdRevenue - totalUberEatsRevenue,
      revenuePercentage: totalUberEatsRevenue ? ((totalPumpdRevenue - totalUberEatsRevenue) / totalUberEatsRevenue) * 100 : 0
    };
  }, [currentData?.rawData]);

  const comparisonData = useMemo(() => {
    return [
      { name: 'Pumpd Net Revenue', value: comparisonMetrics.totalPumpdRevenue },
      { name: 'UberEats Net Revenue', value: comparisonMetrics.totalUberEatsRevenue }
    ];
  }, [comparisonMetrics]);

  const feeComparisonData = useMemo(() => {
    if (!currentData?.rawData?.length) {
      return [];
    }
    
    const data: { name: string, pumpd: number, ubereats: number }[] = [];
    let pumpdTotal = 0;
    let uberTotal = 0;
    
    currentData.rawData.forEach(order => {
      if (order.service_type === 'delivery') {
        const pumpdServiceFee = parseFloat(order.pumpd_commission) || 0;
        const uberServiceFee = parseFloat(order.ubereats_commission) || 0;
        const deliveryFeeOnPumpd = parseFloat(order.delivery_fee_on_pumpd) || 0;
        const uberDeliveryFee = parseFloat(order.ubereats_total_delivery_fees) || 0;
        
        pumpdTotal += pumpdServiceFee + deliveryFeeOnPumpd;
        uberTotal += uberServiceFee + uberDeliveryFee;
      }
    });
    
    data.push(
      { name: 'Service & Delivery Fees', pumpd: pumpdTotal, ubereats: uberTotal }
    );
    
    return data;
  }, [currentData?.rawData]);
  
  const changes = useMemo<ChangeMetrics>(() => {
    const calculatePercentageChange = (current: number, previous: number): string => {
      if (previous === 0) return current > 0 ? '+100%' : '0%';
      
      const percentageChange = ((current - previous) / previous) * 100;
      const formattedChange = percentageChange.toFixed(1);
      
      return percentageChange >= 0 
        ? `+${formattedChange}%` 
        : `${formattedChange}%`;
    };
    
    const determineTrend = (current: number, previous: number): 'up' | 'down' | 'neutral' => {
      if (current > previous) return 'up';
      if (current < previous) return 'down';
      return 'neutral';
    };
    
    if (!currentData || !previousData) {
      return {
        totalOrders: { value: '0%', trend: 'neutral' },
        totalRevenue: { value: '0%', trend: 'neutral' },
        avgOrderValue: { value: '0%', trend: 'neutral' },
        newCustomers: { value: '0%', trend: 'neutral' }
      };
    }
    
    return {
      totalOrders: {
        value: calculatePercentageChange(currentData.totalOrders, previousData.totalOrders),
        trend: determineTrend(currentData.totalOrders, previousData.totalOrders)
      },
      totalRevenue: {
        value: calculatePercentageChange(currentData.totalRevenue, previousData.totalRevenue),
        trend: determineTrend(currentData.totalRevenue, previousData.totalRevenue)
      },
      avgOrderValue: {
        value: calculatePercentageChange(currentData.avgOrderValue, previousData.avgOrderValue),
        trend: determineTrend(currentData.avgOrderValue, previousData.avgOrderValue)
      },
      newCustomers: {
        value: calculatePercentageChange(currentData.newCustomers, previousData.newCustomers),
        trend: determineTrend(currentData.newCustomers, previousData.newCustomers)
      }
    };
  }, [currentData, previousData]);
  
  const refreshData = () => {
    setIsLoading(true);
    // The effect will re-run because isLoading changes
  };
  
  return {
    data: currentData,
    isLoading,
    error,
    dailyData,
    orderTypeData,
    refreshData,
    changes,
    comparisonMetrics,
    comparisonData,
    feeComparisonData,
    restaurantMarkups
  };
};
